// Partners.cshtml (QOR staff): the partners of the chosen processor, each followed by its locations.
// The "Add partner" form is laid out for its content (2026-10-20): the name and its Entity ID side by side (four
// digits, internal; left empty, the API picks a random free one — 2026-10-23), partner or location as a two-way choice
// with the parent shown only for a location, and the processor shown, not offered — it is the one picked at the top
// of the console.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, type ProgramSummary } from '../api';
import { usePageTitle } from '../components/ConsoleLayout';
import { useStaff } from '../components/RequireStaff';
import { ChooseProcessor, FormError } from '../components/ui';

const CODE_PATTERN = '\\d{4}';

const byName = (a: ProgramSummary, b: ProgramSummary) =>
  a.displayName < b.displayName ? -1 : a.displayName > b.displayName ? 1 : 0;

/** Each partner, then its own locations (both by name). */
export function partnerRows(partners: readonly ProgramSummary[]): ProgramSummary[] {
  return partners
    .filter((p) => p.parentProgramId === null)
    .sort(byName)
    .flatMap((parent) => [parent, ...partners.filter((p) => p.parentProgramId === parent.id).sort(byName)]);
}

export function PartnersPage() {
  usePageTitle('Partners');
  const { scope } = useStaff();
  const client = useQueryClient();
  const programs = useQuery({ queryKey: ['programs'], queryFn: adminApi.programs, enabled: !scope.needsProvider });
  const partners = (programs.data ?? []).filter(
    (p) => p.provider !== 'ZumRails' && p.provider === scope.activeProvider,
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [added, setAdded] = useState<{ name: string; id: string | null } | null>(null);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [isLocation, setIsLocation] = useState(false);
  const [parentId, setParentId] = useState('');
  const parents = partners.filter((p) => p.parentProgramId === null).sort(byName);

  async function create(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!scope.activeProvider) return;
    if (isLocation && parentId === '') return setError('Choose the partner this location belongs to.');
    setBusy(true);
    const r = await adminApi.createProgram({
      displayName: name.trim(),
      code,
      primaryProvider: scope.activeProvider,
      parentProgramId: isLocation ? parentId : null,
    });
    setBusy(false);
    if (!r.success) return setError(r.error ?? "Couldn't create this partner.");
    setError(null);
    await client.invalidateQueries({ queryKey: ['programs'] });
    setAdded({ name: name.trim(), id: r.id });
    setName('');
    setCode('');
    setIsLocation(false);
    setParentId('');
  }

  const rows = partnerRows(partners);
  return (
    <>
      <div className="qc-page-heading">
        <h1>Partners</h1>
        <p className="qc-breadcrumb">
          QOR Console <span aria-hidden="true">&rsaquo;</span> <strong>Partners</strong>
        </p>
      </div>

      {scope.needsProvider ? (
        <ChooseProcessor what="to see and manage its partners" />
      ) : (
        <>
          <p className="qc-scope-label">
            Showing <strong>{scope.activeProvider}</strong> partners only
          </p>

          <div className="qc-panel" style={{ marginBottom: 20 }}>
            <div className="qc-panel-title">
              <h3>Add partner</h3>
              <span>A partner cards are issued under, or a location of one</span>
            </div>
            {added && (
              <p className="qc-form-success" role="status">
                <strong>{added.name}</strong> was added.{' '}
                {added.id && (
                  <Link to={`/partners/${added.id}`}>
                    Manage it <span aria-hidden="true">&rsaquo;</span>
                  </Link>
                )}
              </p>
            )}
            <form onSubmit={(e) => void create(e)} className="qc-partner-form">
              <label className="qc-account-field">
                <span>
                  Partner name{' '}
                  <span className="qc-required" aria-hidden="true">
                    *
                  </span>
                </span>
                <input
                  type="text"
                  name="NewDisplayName"
                  placeholder="Acme Financial"
                  autoComplete="off"
                  maxLength={160}
                  required
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setAdded(null);
                  }}
                />
              </label>
              <label className="qc-account-field">
                <span>Entity ID</span>
                <input
                  type="text"
                  name="NewCode"
                  inputMode="numeric"
                  placeholder="Random"
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={4}
                  pattern={CODE_PATTERN}
                  title="Four digits"
                  aria-describedby="partner-code-hint"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                />
                <small id="partner-code-hint" className="qc-field-hint">
                  Internal, four digits. Leave empty for a random one; it can be changed on Profile.
                </small>
              </label>

              <fieldset className="qc-account-field qc-span-2 qc-partner-kind">
                <legend>Type</legend>
                <div className="qc-segmented" role="group" aria-label="Type">
                  <button type="button" aria-pressed={!isLocation} onClick={() => setIsLocation(false)}>
                    Partner
                  </button>
                  <button
                    type="button"
                    aria-pressed={isLocation}
                    disabled={parents.length === 0}
                    title={parents.length === 0 ? 'Add a partner first: a location belongs to one.' : undefined}
                    onClick={() => setIsLocation(true)}
                  >
                    Location of a partner
                  </button>
                </div>
                <small className="qc-field-hint">
                  {isLocation
                    ? "A location shares its partner's settings unless it has its own; the partner's staff manage it."
                    : 'A top-level partner: its own settings, staff and cardholders.'}
                </small>
              </fieldset>

              {isLocation && (
                <label className="qc-account-field">
                  <span>
                    Under partner{' '}
                    <span className="qc-required" aria-hidden="true">
                      *
                    </span>
                  </span>
                  <select
                    name="NewParentProgramId"
                    required
                    value={parentId}
                    onChange={(e) => setParentId(e.target.value)}
                  >
                    <option value="" disabled>
                      Choose a partner…
                    </option>
                    {parents.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.displayName}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {error && (
                <div className="qc-span-2">
                  <FormError message={error} />
                </div>
              )}
              <div className="qc-form-actions qc-span-2">
                <span className="qc-processor-chip">
                  Processor <strong>{scope.activeProvider}</strong>
                  <span className="qc-field-hint">the one picked at the top</span>
                </span>
                <button type="submit" className="qor-button qor-button-primary" disabled={busy}>
                  {busy ? 'Adding…' : 'Add partner'}
                </button>
              </div>
            </form>
          </div>

          <div className="qc-panel">
            {programs.isPending ? null : rows.length === 0 ? (
              <p className="qc-empty">No partners yet. Add one above to get started.</p>
            ) : (
              <>
                <table className="qc-plain-table qc-partner-table">
                  <thead>
                    <tr>
                      <th>Partner</th>
                      <th>Entity ID</th>
                      <th>Processor</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((p) => (
                      <tr key={p.id}>
                        <td>
                          {p.parentProgramId !== null ? (
                            <span style={{ color: 'var(--qor-muted)', paddingLeft: 20 }}>&#8627; {p.displayName}</span>
                          ) : (
                            <>
                              <span className="qc-row-dot" aria-hidden="true"></span>
                              {p.displayName}
                            </>
                          )}
                        </td>
                        <td>
                          <code className="qc-code-chip">{p.code}</code>
                        </td>
                        <td>{p.provider}</td>
                        <td>
                          <span className={`qc-badge ${p.isActive ? 'qc-badge-success' : 'qc-badge-muted'}`}>
                            {p.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <Link to={`/partners/${p.id}`}>
                            Manage <span aria-hidden="true">&rsaquo;</span>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="qc-pagination">
                  <span>
                    Showing {rows.length} of {rows.length} partners
                  </span>
                  <div className="qc-pagination-links">
                    <button type="button" className="qc-button-ghost" disabled>
                      Previous
                    </button>
                    <button type="button" className="qc-button-ghost" disabled>
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
}
